import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Select from 'react-select';
import { Form, Button, Table, Row, Col } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';

const PlayerStatUpload = () => {
  const [players, setPlayers] = useState([]);
  const [entries, setEntries] = useState([]);
  const [newEntry, setNewEntry] = useState({
    player: null,
    wins: '',
    losses: '',
    kills: '',
    deaths: '',
    month: ''
  });
  const navigate = useNavigate();

  // Fetch players on mount
  useEffect(() => {
    api.get('/api/players')
      .then(res => {
        const options = res.data.map(player => ({
          value: player.id,
          label: player.name
        })).sort((a, b) => a.label.localeCompare(b.label));
        setPlayers(options);
      })
      .catch(err => {
        if (err.response?.status === 401) {
          console.warn('Unauthorized — redirecting to login...');
          navigate('/login');
        } else {
          console.error("Error fetching players:", err);
        }
      });
  }, [navigate]);

  const handleChange = (field, value) => {
    setNewEntry(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addEntry = () => {
    if (!newEntry.player || !newEntry.month || !newEntry.wins || !newEntry.losses || !newEntry.kills || !newEntry.deaths) {
      alert('All fields are required.');
      return;
    }

    const exists = entries.find(e =>
      e.player.value === newEntry.player.value &&
      e.month === newEntry.month
    );

    if (exists) {
      if (!window.confirm("This entry already exists in your list. Overwrite it?")) return;
      setEntries(entries.map(e =>
        (e.player.value === newEntry.player.value && e.month === newEntry.month)
          ? newEntry
          : e
      ));
    } else {
      setEntries(prev => [...prev, newEntry]);
    }

    setNewEntry({
      player: null,
      wins: '',
      losses: '',
      kills: '',
      deaths: '',
      month: ''
    });
  };

  const removeEntry = (index) => {
    setEntries(entries.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    const payload = entries.map(e => ({
      playerId: e.player.value,
      wins: Number(e.wins),
      losses: Number(e.losses),
      kills: Number(e.kills),
      deaths: Number(e.deaths),
      month: e.month
    }));

    try {
      await api.post('/api/stats/bulk', payload);
      alert('Stats uploaded successfully!');
      setEntries([]);
    } catch (error) {
      if (error.response?.status === 401) {
        console.warn('Unauthorized — redirecting to login...');
        navigate('/login');
      } else {
        console.error("Upload error:", error);
        alert("Failed to upload stats.");
      }
    }
  };

  return (
    <div className="container mt-4">
      <h2 className="mb-4">Upload Player Stats</h2>

      <Form>
        <Row className="mb-3">
          <Col md={4}>
            <Form.Label>Player</Form.Label>
            <Select
              options={players}
              value={newEntry.player}
              onChange={val => handleChange('player', val)}
              placeholder="Select player..."
            />
          </Col>
          <Col md={2}>
            <Form.Label>Wins</Form.Label>
            <Form.Control
              type="number"
              value={newEntry.wins}
              onChange={(e) => handleChange('wins', e.target.value)}
              min="0"
            />
          </Col>
          <Col md={2}>
            <Form.Label>Losses</Form.Label>
            <Form.Control
              type="number"
              value={newEntry.losses}
              onChange={(e) => handleChange('losses', e.target.value)}
              min="0"
            />
          </Col>
          <Col md={2}>
            <Form.Label>Kills</Form.Label>
            <Form.Control
              type="number"
              value={newEntry.kills}
              onChange={(e) => handleChange('kills', e.target.value)}
              min="0"
            />
          </Col>
          <Col md={2}>
            <Form.Label>Deaths</Form.Label>
            <Form.Control
              type="number"
              value={newEntry.deaths}
              onChange={(e) => handleChange('deaths', e.target.value)}
              min="0"
            />
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={4}>
            <Form.Label>Month</Form.Label>
            <Form.Control
              type="month"
              value={newEntry.month}
              onChange={(e) => handleChange('month', e.target.value)}
            />
          </Col>
          <Col md={2} className="d-flex align-items-end">
            <Button variant="success" onClick={addEntry}>Add</Button>
          </Col>
        </Row>
      </Form>

      {entries.length > 0 && (
        <>
          <h4 className="mt-4">Stats To Upload</h4>
          <Table striped bordered>
            <thead>
              <tr>
                <th>Player</th>
                <th>Wins</th>
                <th>Losses</th>
                <th>Kills</th>
                <th>Deaths</th>
                <th>Month</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => (
                <tr key={i}>
                  <td>{e.player.label}</td>
                  <td>{e.wins}</td>
                  <td>{e.losses}</td>
                  <td>{e.kills}</td>
                  <td>{e.deaths}</td>
                  <td>{e.month}</td>
                  <td>
                    <Button variant="danger" size="sm" onClick={() => removeEntry(i)}>Remove</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <Button variant="primary" onClick={handleSubmit}>Submit All</Button>
        </>
      )}
    </div>
  );
};

export default PlayerStatUpload;
